import { useCallback, useEffect, useState } from 'react';
import { App, Button, Input, Skeleton, Space, Typography } from 'antd';
import { SaveOutlined } from '@ant-design/icons';
import { getTenantProfile, updateTenantProfile } from '@/api/tenantAdmin';
import { isNotAvailable, normalizeApiError } from '@/api/errors';
import type { TenantProfile } from '@/api/types';
import { EmptyState } from '@/components/EmptyState';

const { Title, Paragraph, Text } = Typography;

/** Pretty-print a wire JSON document for the editor; empty string for absent/empty. */
function toEditorText(doc: Record<string, unknown> | null | undefined): string {
  return doc && Object.keys(doc).length ? JSON.stringify(doc, null, 2) : '';
}

/**
 * Parse a JSON-object textarea. Returns `{ ok: true, value }` (undefined for blank — the PUT
 * omits the field, keeping the stored value) or `{ ok: false, error }` which must BLOCK the save.
 */
function parseJsonField(
  raw: string,
  label: string,
): { ok: true; value?: Record<string, unknown> } | { ok: false; error: string } {
  const trimmed = raw.trim();
  if (!trimmed) return { ok: true, value: undefined };
  try {
    const parsed: unknown = JSON.parse(trimmed);
    if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
      return { ok: true, value: parsed as Record<string, unknown> };
    }
    return { ok: false, error: `${label} must be a JSON object.` };
  } catch {
    return { ok: false, error: `${label} is not valid JSON.` };
  }
}

/**
 * B1 · F-TA-03 — regime / compliance-profile editor over GET/PUT /v1/tenants/{hub}/profile.
 * `complianceProfile`, `mlroCalendar` and `defaults` are JSON OBJECTS on the wire (jsonb
 * documents) — edited as validated-JSON textareas (pretty-printed on load; invalid JSON blocks the
 * save with an inline error, never a 400 round-trip or an "[object Object]" render).
 */
export function ProfilePanel() {
  const { message } = App.useApp();
  const [regime, setRegime] = useState('');
  const [complianceText, setComplianceText] = useState('');
  const [complianceError, setComplianceError] = useState<string | null>(null);
  const [calendarText, setCalendarText] = useState('');
  const [calendarError, setCalendarError] = useState<string | null>(null);
  const [defaultsText, setDefaultsText] = useState('');
  const [defaultsError, setDefaultsError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [unavailable, setUnavailable] = useState(false);

  const apply = useCallback((p: TenantProfile) => {
    setRegime(p.regime ?? '');
    setComplianceText(toEditorText(p.complianceProfile));
    setCalendarText(toEditorText(p.mlroCalendar));
    setDefaultsText(toEditorText(p.defaults));
    setComplianceError(null);
    setCalendarError(null);
    setDefaultsError(null);
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    setUnavailable(false);
    try {
      apply(await getTenantProfile());
    } catch (e) {
      const n = normalizeApiError(e);
      if (isNotAvailable(n)) setUnavailable(true);
      else message.error(n.message);
    } finally {
      setLoading(false);
    }
    // message stable
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [apply]);

  useEffect(() => {
    void load();
  }, [load]);

  const save = useCallback(async () => {
    const compliance = parseJsonField(complianceText, 'Compliance profile');
    const calendar = parseJsonField(calendarText, 'MLRO review calendar');
    const defaults = parseJsonField(defaultsText, 'Tenant defaults');
    setComplianceError(compliance.ok ? null : compliance.error);
    setCalendarError(calendar.ok ? null : calendar.error);
    setDefaultsError(defaults.ok ? null : defaults.error);
    if (!compliance.ok || !calendar.ok || !defaults.ok) return;
    setSaving(true);
    try {
      const res = await updateTenantProfile({
        regime: regime.trim() || undefined,
        complianceProfile: compliance.value,
        mlroCalendar: calendar.value,
        defaults: defaults.value,
      });
      apply(res);
      message.success('Profile saved.');
    } catch (e) {
      const n = normalizeApiError(e);
      if (isNotAvailable(n)) message.info('The tenant-profile endpoint is not live yet (Track A pending).');
      else message.error(n.message);
    } finally {
      setSaving(false);
    }
  }, [regime, complianceText, calendarText, defaultsText, apply, message]);

  if (loading) return <Skeleton active paragraph={{ rows: 5 }} />;

  if (unavailable) {
    return (
      <EmptyState
        icon={<SaveOutlined />}
        title="Regime & compliance profile"
        description="The hub's regime, compliance profile and MLRO review calendar land here once the tenant-admin API is live (Track A, PS-4). These defaults cascade into every flow, cutting per-flow config."
        bullets={[
          'Regime (e.g. IN-RBI) and a structured compliance profile',
          'MLRO periodic-review calendar',
          'Structured tenant defaults inherited by flows',
        ]}
      />
    );
  }

  return (
    <div style={{ maxWidth: 640 }}>
      <Title level={5} style={{ margin: 0 }}>
        Regime & compliance profile
      </Title>
      <Paragraph type="secondary" style={{ margin: '4px 0 16px', fontSize: 13 }}>
        Hub-wide defaults. These cascade into flow authoring and the review cadence — set them once here
        instead of per flow.
      </Paragraph>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
        <Field label="Regime" hint="The regulatory regime this hub operates under (e.g. IN-RBI, UK-FCA).">
          <Input value={regime} onChange={(e) => setRegime(e.target.value)} placeholder="IN-RBI" />
        </Field>
        <JsonField
          label="Compliance profile (JSON)"
          hint="A structured profile document that packages obligation defaults. A JSON object, or leave blank."
          placeholder='{ "riskAppetite": "medium" }'
          value={complianceText}
          error={complianceError}
          onChange={(v) => {
            setComplianceText(v);
            if (complianceError) setComplianceError(null);
          }}
        />
        <JsonField
          label="MLRO review calendar (JSON)"
          hint="The periodic-review calendar document (cadences, owners). A JSON object, or leave blank."
          placeholder='{ "cadence": "quarterly" }'
          value={calendarText}
          error={calendarError}
          onChange={(v) => {
            setCalendarText(v);
            if (calendarError) setCalendarError(null);
          }}
        />
        <JsonField
          label="Tenant defaults (JSON)"
          hint="Advanced: structured defaults inherited by flows. A JSON object, or leave blank."
          placeholder='{ "retentionDays": 3650 }'
          value={defaultsText}
          error={defaultsError}
          onChange={(v) => {
            setDefaultsText(v);
            if (defaultsError) setDefaultsError(null);
          }}
        />
      </div>

      <Space style={{ marginTop: 18 }}>
        <Button type="primary" icon={<SaveOutlined />} loading={saving} onClick={save}>
          Save profile
        </Button>
        <Button onClick={() => void load()} disabled={saving}>
          Reset
        </Button>
      </Space>
    </div>
  );
}

function Field({ label, hint, children }: { label: string; hint?: string; children: React.ReactNode }) {
  return (
    <label style={{ display: 'block' }}>
      <div style={{ fontSize: 12.5, fontWeight: 600, marginBottom: 4 }}>{label}</div>
      {hint && (
        <div style={{ fontSize: 11.5, color: 'var(--ol-text-tertiary)', marginBottom: 6 }}>{hint}</div>
      )}
      {children}
    </label>
  );
}

function JsonField({
  label,
  hint,
  placeholder,
  value,
  error,
  onChange,
}: {
  label: string;
  hint: string;
  placeholder: string;
  value: string;
  error: string | null;
  onChange: (v: string) => void;
}) {
  return (
    <Field label={label} hint={hint}>
      <Input.TextArea
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        autoSize={{ minRows: 3, maxRows: 10 }}
        className="ol-mono"
        status={error ? 'error' : undefined}
      />
      {error && (
        <Text type="danger" style={{ fontSize: 11.5 }}>
          {error}
        </Text>
      )}
    </Field>
  );
}
